'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { SearchIcon } from './components';
import { ChevronDownIcon, CheckIcon } from './icons';

export interface PlatformPickerOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * A platform picker built for this card, not borrowed from the panel's own
 * `Select`. That one renders its open list through a portal straight onto
 * `document.body` — right for a table row it must escape the overflow of,
 * wrong here: a portal leaves the dark theme's token overrides behind, since
 * those are scoped to `.authpage`'s own subtree, and the panel's light
 * `--surface` is what a page outside it falls back to. This one never
 * leaves the card, so it never leaves the theme either.
 */
export function PlatformPicker({
  value,
  onChange,
  options,
  id,
  ariaLabel,
  searchPlaceholder = 'Search platforms',
}: {
  value: string;
  onChange: (value: string) => void;
  options: PlatformPickerOption[];
  id?: string;
  ariaLabel?: string;
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const selected = options.find((option) => option.value === value) ?? options[0];
  const needle = query.trim().toLowerCase();
  const shown = needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;

  // Closes on an outside click and on Escape -- the two ways every other
  // picker in this product closes, so this one does not feel like a
  // different control wearing the same colours.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
  }, [open]);

  return (
    <div className="platform-picker" ref={rootRef}>
      <button
        type="button"
        id={id}
        className="platform-picker-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="platform-picker-value">
          {selected.icon}
          {selected.label}
        </span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div className="platform-picker-pop">
          <div className="platform-picker-search">
            <SearchIcon />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>
          <div id={listId} className="platform-picker-list" role="listbox">
            {shown.map((option) => (
              <button
                type="button"
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                className={`platform-picker-option${option.value === value ? ' is-selected' : ''}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                {option.icon}
                {option.label}
                {option.value === value && <CheckIcon />}
              </button>
            ))}
            {shown.length === 0 && <p className="platform-picker-empty">No match.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
